// ── usePolyBalance — the Polymarket twin of the Kalshi balance readout ──
//
// Resolution order mirrors trading itself: the ORG credential pool when the
// org has poly keys (relay GET /api/polymarket/balance — account-level
// funder USDC), falling back to the browser-side personal connection's
// balance (polyAccountStore) when the relay reports the org unconfigured.
// "If the org key is enabled, then it's on the org key" (Brendan, Jul-8).
//
// Poll cadence is deliberately slow (30s, visibility-aware): balance moves
// on fills/settlements, not ticks, and the relay call is a live CLOB
// round-trip on the org's read quota. The result is cached MODULE-WIDE so
// the hook can mount on several always-on chrome surfaces (rail header,
// status bar, key-mode banner, VD widget) without multiplying that CLOB
// call — concurrent mounts share one fetch per TTL window.

import { useEffect, useState } from 'react';
import { getPolyBalanceViaRelay } from '@/lib/polymarket/relay';
import { usePolyAccountStore } from '@/stores/polyAccountStore';
import { useAuthStore } from '@/stores/authStore';
import { startVisibilityAwareInterval } from '@/lib/visibilityThrottle';

const POLL_MS = 30_000;
// Shared-result window: slightly under the poll period so a single mount
// still refreshes every tick, while N mounts coalesce onto one fetch.
const SHARE_TTL_MS = 25_000;

type RelayResult = { usd: number } | 'unavailable' | 'hidden';

// Every shared value is stamped with the identity (user|org) it was read for:
// a different trader or org on this tab never inherits the previous one's
// balance or "unavailable" verdict, and a read that resolves after the
// identity changed is dropped rather than cached.
let _cached: { identity: string; at: number; result: RelayResult } | null = null;
let _inflight: { identity: string; promise: Promise<RelayResult | null> } | null = null;

function currentIdentity(): string | null {
  const user = useAuthStore.getState().user;
  return user ? `${user.id}|${user.orgId ?? ''}` : null;
}

function freshCached(identity: string | null): RelayResult | null {
  return _cached && _cached.identity === identity && Date.now() - _cached.at < SHARE_TTL_MS
    ? _cached.result
    : null;
}

/** Drop the shared balance (sign-out / identity replacement). It runs from
 *  authStore's deferred purge, possibly after the new identity's first read
 *  started, so only a read for a different identity is dropped. */
export function resetPolyBalanceCache(): void {
  const identity = currentIdentity();
  if (_cached && _cached.identity !== identity) _cached = null;
  if (_inflight && _inflight.identity !== identity) _inflight = null;
}

async function fetchRelayShared(identity: string): Promise<RelayResult | null> {
  const fresh = freshCached(identity);
  if (fresh) return fresh;
  if (_inflight?.identity === identity) return _inflight.promise;
  const promise = getPolyBalanceViaRelay()
    .then((out): RelayResult | null => {
      if (currentIdentity() !== identity) return null;
      if (out.ok) {
        _cached = { identity, at: Date.now(), result: { usd: out.balance.collateralUsd } };
        return _cached.result;
      }
      if ('hidden' in out) {
        // #1205: a manager hid the org balance — a permission answer, cached
        // like the others, never a transient error.
        _cached = { identity, at: Date.now(), result: 'hidden' };
        return _cached.result;
      }
      if (out.code) {
        // Unavailable (org unconfigured / geoblock explainer) — cache it
        // too; no point re-asking every mount within the window.
        _cached = { identity, at: Date.now(), result: 'unavailable' };
        return _cached.result;
      }
      // Transient error: no cache write — callers keep their last-good.
      return null;
    })
    .finally(() => {
      if (_inflight?.promise === promise) _inflight = null;
    });
  _inflight = { identity, promise };
  return promise;
}

export interface PolyBalanceState {
  /** Funder USDC balance in dollars. Null until a source resolves. */
  usd: number | null;
  /** Which key path produced the number. Null while unresolved. */
  source: 'org' | 'personal' | null;
  /** True once the relay has ANSWERED that no org poly path exists (and no
   *  personal connection covers it) — distinguishes "no poly configured"
   *  from "still loading" for status indicators. */
  unavailable: boolean;
  /** #1205: a manager hid the organization balance from this trader. `usd`
   *  is null and no personal figure stands in for it; `source` stays 'org'
   *  because the org path answered. */
  hidden: boolean;
}

export function usePolyBalance(enabled: boolean = true): PolyBalanceState {
  const identity = useAuthStore((st) => (st.user ? `${st.user.id}|${st.user.orgId ?? ''}` : null));
  const [relayFor, setRelayFor] = useState<{ identity: string | null; result: RelayResult } | null>(() => {
    // Adopt the shared cache synchronously so late-mounting chrome doesn't
    // flash a loading state the app already knows the answer to.
    const fresh = freshCached(identity);
    return fresh ? { identity, result: fresh } : null;
  });
  const relay = relayFor?.identity === identity ? relayFor.result : null;
  const personal = usePolyAccountStore((s) => s.funderBalance);

  useEffect(() => {
    if (!enabled || identity == null) return;
    let stopped = false;
    // Per identity run: a previous identity's pending read must not hold off
    // the new identity's first read until the next poll.
    let inFlight = false;
    const tick = () => {
      if (stopped || inFlight) return;
      inFlight = true;
      void fetchRelayShared(identity)
        .then((result) => {
          if (stopped || result === null) return;
          setRelayFor({ identity, result });
        })
        .finally(() => {
          inFlight = false;
        });
    };
    tick();
    const stop = startVisibilityAwareInterval(tick, POLL_MS);
    return () => {
      stopped = true;
      stop();
    };
  }, [enabled, identity]);

  if (relay === 'hidden') return { usd: null, source: 'org', unavailable: false, hidden: true };
  if (relay && relay !== 'unavailable') return { usd: relay.usd, source: 'org', unavailable: false, hidden: false };
  if (personal) return { usd: personal.collateralUsd, source: 'personal', unavailable: false, hidden: false };
  return { usd: null, source: null, unavailable: relay === 'unavailable', hidden: false };
}
